/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand, SideNavSection } from '@repo/shared-client/components/blocks'
import {
  InputFormField,
  NativeSelectFormField,
  SliderFormField,
  SwitchFormField,
  TagMultiSelectFormField,
  tagMultiSelectFromTags,
} from '@repo/shared-client/components/fields'
import { Button, Card, CardContent } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { SFMapBoxSearchBoxInputTheme } from '@repo/shared-client/mapbox'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { usePlaceCategoryOptions } from '@web/src/features/place/use-place-category-options'
import { useTranslation } from '@web/src/lib/intl/react'
import { FolderOpenIcon, FunnelPlusIcon, ListOrderedIcon, MapIcon, MapPinIcon, TagIcon } from 'lucide-react'
import { type ReactNode, useCallback } from 'react'
import SFInfoPopup from 'src/components/primatives/sf-info-popup/sf-info-popup'
import SFLocationSearchField from 'src/components/primatives/sf-location-search-field/sf-location-search-field'
import {
  PLACE_LIST_DISTANCE_UNLIMITED_FOR_APPLY,
  PLACE_LIST_LOCATION_EMPTY_FOR_APPLY,
  type PlaceListFilterFormValues,
} from '../place-list-filter-types'
import type { PlaceListSideNavProps } from '../place-list-side-nav/place-list-side-nav'
import { usePlaceListFilterFields, usePlaceListFilterForm } from '../use-place-list-filter-form'

export type PlaceListTopNavProps = PlaceListSideNavProps & {
  rightBarChildren?: ReactNode
  /** When false, hides the Sort By control (e.g. map route uses a fixed server sort). */
  sortByEnabled?: boolean
}

function PlaceListTopNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
  distanceWithinEnabled = true,
  distanceFromEnabled = true,
  sortByEnabled = true,
}: Omit<PlaceListTopNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { reset } = useFormContext<PlaceListFilterFormValues>()
  const onFiltersApplyResolved = useCallback(
    (values: PlaceListFilterFormValues) => {
      let next = values
      if (!distanceWithinEnabled) {
        next = { ...next, distance: PLACE_LIST_DISTANCE_UNLIMITED_FOR_APPLY }
      }
      if (!distanceFromEnabled) {
        next = { ...next, location: PLACE_LIST_LOCATION_EMPTY_FOR_APPLY }
      }
      onFiltersApply(next)
    },
    [distanceFromEnabled, distanceWithinEnabled, onFiltersApply]
  )
  const { triggerFilterChange, distance, handleSearchChange } = usePlaceListFilterFields(onFiltersApplyResolved)

  const { selectOptions: categoryOptions } = usePlaceCategoryOptions()

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <SearchExpand
        clearSearchLabel={t('Clear search')}
        filterControlsDisabled={filtersDisabled}
        inputProps={{ 'aria-label': t('Search places') }}
        minChars={0}
        name="name"
        onSearchChange={handleSearchChange}
        placeholder={t('Search by name...')}
        rightBarChildren={rightBarChildren}
        toggleFiltersLabel={t('Toggle filters')}
      >
        <Card>
          <CardContent className="flex flex-col gap-4">
            {sortByEnabled && (
              <SideNavSection className="my-0" icon={<ListOrderedIcon />} label={t('Sort By')}>
                <NativeSelectFormField
                  disabled={filtersDisabled}
                  name="sortBy"
                  onValueChange={() => triggerFilterChange()}
                >
                  <option value="DISTANCE">{t('Distance')}</option>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="NAME">{t('Name')}</option>
                  <option value="RATING">{t('Rating')}</option>
                </NativeSelectFormField>
              </SideNavSection>
            )}

            {distanceWithinEnabled && (
              <SideNavSection className="my-0" icon={<MapPinIcon />} label={t('Distance Within')}>
                <div className="flex w-full items-center justify-between gap-4">
                  <SliderFormField
                    disabled={filtersDisabled}
                    name="distance"
                    onValueChangeEnd={() => triggerFilterChange()}
                    sliderProps={{
                      className: 'flex-1 min-w-0',
                      max: 1000,
                      min: 10,
                    }}
                  />
                  <span className="min-w-[50px] whitespace-nowrap text-right">
                    {distance?.[0] < 1000 ? t('{{value}} km', { value: distance[0] }) : t('ALL')}
                  </span>
                </div>
              </SideNavSection>
            )}

            {distanceFromEnabled && (
              <SideNavSection className="my-0" icon={<MapIcon />} label={t('Distance From')}>
                {hasPermission('location:change-location') ? (
                  <div className={cn('w-full', filtersDisabled && 'pointer-events-none opacity-60')}>
                    <SFLocationSearchField
                      name="location"
                      onLocationChange={() => triggerFilterChange()}
                      placeholder={t('Enter starting location')}
                      theme={SFMapBoxSearchBoxInputTheme}
                    />
                  </div>
                ) : (
                  <div className="flex w-full items-center justify-center">
                    <InputFormField
                      disabled
                      inputProps={{
                        value: isAuthenticated ? defaultLocation : t('Guelph, Ontario, Canada'),
                        readOnly: true,
                      }}
                      name="locationPlaceholder"
                    />
                    <SFInfoPopup
                      description={
                        isAuthenticated
                          ? t('Adjusting this is available to supporters only')
                          : t('You must be member to adjust the starting location, join for free.')
                      }
                      iconButtonProps={{ className: 'mt-7' }}
                      title={isAuthenticated ? t('Supporters Only') : t('Members Only')}
                    />
                  </div>
                )}
              </SideNavSection>
            )}

            <SideNavSection className="my-0" icon={<FolderOpenIcon />} label={t('Category')}>
              <TagMultiSelectFormField
                disabled={filtersDisabled}
                name="categories"
                onValueChange={() => triggerFilterChange()}
                onValueChangeTrigger="close"
                options={categoryOptions}
                placeholder={t('All')}
              />
            </SideNavSection>

            <SideNavSection className="my-0" icon={<TagIcon />} label={t('Tag Filter')}>
              <TagMultiSelectFormField
                disabled={filtersDisabled}
                name="tags"
                onValueChange={() => triggerFilterChange()}
                onValueChangeTrigger="close"
                options={tagMultiSelectFromTags(tags)}
                placeholder={t('All')}
              />
            </SideNavSection>

            <SideNavSection className="my-0" icon={<FunnelPlusIcon />} label={t('Other Filters')}>
              <div className="flex flex-col items-stretch gap-3 py-1">
                {isAuthenticated && (
                  <SwitchFormField
                    disabled={filtersDisabled}
                    label={t('Places I Maintain')}
                    name="myPlaces"
                    onCheckedChange={(checked) => triggerFilterChange({ myPlaces: checked })}
                  />
                )}

                {hasPermission('place:review') && (
                  <SwitchFormField
                    disabled={filtersDisabled}
                    label={t('Needs Review')}
                    name="needsReview"
                    onCheckedChange={(checked) => triggerFilterChange({ needsReview: checked })}
                  />
                )}
              </div>
            </SideNavSection>

            {isAuthenticated && (
              <div className="flex w-full items-center justify-end">
                <Button onClick={handleResetFilters} type="button" variant="secondary">
                  {t('Reset Filters')}
                </Button>
              </div>
            )}
            {!isAuthenticated && <SFJoinButton className="w-full" size="sm" />}
          </CardContent>
        </Card>
      </SearchExpand>
    </div>
  )
}

export default function PlaceListTopNav({
  tags,
  isAuthenticated,
  hasPermission,
  defaultLocation,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
  distanceWithinEnabled = true,
  distanceFromEnabled = true,
  sortByEnabled = true,
}: PlaceListTopNavProps) {
  const formMethods = usePlaceListFilterForm(filterDefaults)

  return (
    <Form formMethods={formMethods} suppressHydrationWarning>
      <PlaceListTopNavFields
        defaultLocation={defaultLocation}
        distanceFromEnabled={distanceFromEnabled}
        distanceWithinEnabled={distanceWithinEnabled}
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        rightBarChildren={rightBarChildren}
        sortByEnabled={sortByEnabled}
        tags={tags}
      />
    </Form>
  )
}
